import { BaseAPI } from "./BaseAPI";

import type { APIConf } from "./BaseAPI";
import type { EmptyObject } from "../types";
import type {
  AddScheduleListMediaParams,
  AddScheduleListMediaResponse,
  ArchiveAdBreakResponse,
  BroadcastAdService,
  CreatePlaylistParams,
  DeleteAdBreakResponse,
  DeleteScheduleListMediaParams,
  DeleteScheduleListMediaResponse,
  DeleteScheduleListTimeslotParams,
  DeleteScheduleListTimeslotResponse,
  DeleteScheduleParams,
  DeleteScheduleResponse,
  GetAdBreakResponse,
  GetAdBreaksListResponse,
  GetBroadcastsResponse,
  GetCalendarFillingParams,
  GetCalendarFillingResponse,
  GetCalendarFillingWeekParams,
  GetCalendarFillingWeekResponse,
  GetChannelParams,
  GetChannelResponse,
  GetChannelsListResponse,
  GetPlaylistListResponse,
  GetPlaylistResponse,
  GetRatingListResponse,
  GetScheduleAllScheduleListParams,
  GetScheduleAllScheduleListResponse,
  GetScheduleByIdParams,
  GetScheduleByIdResponse,
  GetScheduleDetailsParams,
  GetScheduleDetailsResponse,
  GetScheduleEPGParams,
  GetScheduleEPGResponse,
  GetScheduleKpisParams,
  GetScheduleKpisResponse,
  GetSchedulePreviewParams,
  GetSchedulePreviewResponse,
  GetScheduleScheduleListParams,
  GetScheduleScheduleListResponse,
  GetSchedulesParams,
  GetSchedulesResponse,
  GetTemplateResponse,
  GetTemplatesAssetListResponse,
  GetTemplatesListResponse,
  GetTimeslotList,
  GetTimeslotResponse,
  OVPBroadcast,
  SchedulingParameters,
  UpdateBroadcastResponse,
  UpdateChannelParams,
  UpdatedAdBreak,
  UpdatedTemplate,
  UpdatedTimeslot,
  UpdatePlaylistParams,
  UpdateScheduleListMediaParams,
  UpdateScheduleListMediaResponse,
  UpdateSchedulesParams,
} from "./FastAPI.types";

class FastAPIClass extends BaseAPI {
  constructor(conf: APIConf) {
    super(conf);
    this.service = "fast";
    this.version = "v1";
  }

  async getChannelsList(): Promise<GetChannelsListResponse> {
    return this.axios
      .get<GetChannelsListResponse>(`${this.routeBase}/admin/fast_channels`)
      .then((response) => response.data);
  }

  async getChannel(channelId: GetChannelParams): Promise<GetChannelResponse> {
    return this.axios
      .get<GetChannelResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}`,
      )
      .then((response) => response.data);
  }

  async updateChannel({
    channelId,
    data,
  }: UpdateChannelParams): Promise<unknown> {
    return this.axios
      .put(`${this.routeBase}/admin/fast_channel/${channelId}`, data)
      .then((response) => response.data);
  }

  async getSchedules({
    channelId,
  }: GetSchedulesParams): Promise<GetSchedulesResponse> {
    return this.axios
      .get<GetSchedulesResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedules`,
      )
      .then((response) => response.data);
  }

  async updateSchedules({
    channelId,
    updatedScheduleList,
  }: UpdateSchedulesParams): Promise<GetSchedulesResponse> {
    return this.axios
      .put<GetSchedulesResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedules`,
        updatedScheduleList,
      )
      .then((response) => response.data);
  }

  async getScheduleById({
    channelId,
    scheduleId,
  }: GetScheduleByIdParams): Promise<GetScheduleByIdResponse> {
    return this.axios
      .get<GetScheduleByIdResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedules/${scheduleId}`,
      )
      .then((response) => response.data);
  }

  async getScheduleKpis({
    channelId,
    scheduleId,
    day,
    month,
    week,
  }: GetScheduleKpisParams): Promise<GetScheduleKpisResponse> {
    return this.axios
      .get<GetScheduleKpisResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/${scheduleId}/kpis`,
        {
          params: {
            day,
            month,
            week,
          },
        },
      )
      .then((response) => response.data);
  }

  async getScheduleDetails({
    channelId,
    day,
  }: GetScheduleDetailsParams): Promise<GetScheduleDetailsResponse> {
    return this.axios
      .get<GetScheduleDetailsResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedules/details`,
        {
          params: {
            day,
          },
        },
      )
      .then((response) => response.data);
  }

  async getScheduleScheduleList({
    channelId,
    scheduleId,
    periodStart,
    periodEnd,
  }: GetScheduleScheduleListParams): Promise<GetScheduleScheduleListResponse> {
    return this.axios
      .get<GetScheduleScheduleListResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/${scheduleId}/schedule_list`,
        {
          params: {
            period_start: periodStart,
            period_end: periodEnd,
          },
        },
      )
      .then((response) => response.data);
  }

  async getScheduleAllScheduleList({
    channelId,
    periodStart,
    periodEnd,
  }: GetScheduleAllScheduleListParams): Promise<GetScheduleAllScheduleListResponse> {
    return this.axios
      .get<GetScheduleAllScheduleListResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedules_lists`,
        {
          params: {
            period_start: periodStart,
            period_end: periodEnd,
          },
        },
      )
      .then((response) => response.data);
  }

  async addScheduleListMedia({
    channelId,
    scheduleId,
    media,
  }: AddScheduleListMediaParams): Promise<AddScheduleListMediaResponse> {
    return this.axios
      .put<AddScheduleListMediaResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/${scheduleId}/schedule_list`,
        media,
      )
      .then((response) => response.data);
  }

  async deleteScheduleListMedia({
    channelId,
    scheduleId,
    uuid,
  }: DeleteScheduleListMediaParams): Promise<DeleteScheduleListMediaResponse> {
    return this.axios
      .delete<DeleteScheduleListMediaResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/${scheduleId}/schedule_list/${uuid}`,
      )
      .then((response) => response.data);
  }

  async updateScheduleListMedia({
    channelId,
    scheduleId,
    uuid,
    media,
  }: UpdateScheduleListMediaParams): Promise<UpdateScheduleListMediaResponse> {
    return this.axios
      .post<UpdateScheduleListMediaResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/${scheduleId}/schedule_list/${uuid}`,
        media,
      )
      .then((response) => response.data);
  }

  async getSchedulePreview({
    channelId,
    scheduleId,
    uuid,
  }: GetSchedulePreviewParams): Promise<GetSchedulePreviewResponse> {
    return this.axios
      .get<GetSchedulePreviewResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/${scheduleId}/schedule_list/${uuid}/preview`,
      )
      .then((response) => response.data);
  }

  async uploadChannelLogo({
    channelId,
    base64Logo,
    onUploadProgress,
  }: {
    channelId: string;
    base64Logo: string;
    onUploadProgress: (progressEvent: ProgressEvent) => void;
  }): Promise<{ message: string }> {
    return this.axios
      .post<{ message: string }>(
        `${this.routeBase}/admin/fast_channel/${channelId}/logo`,
        { logo: base64Logo },
        {
          onUploadProgress,
        },
      )
      .then((response) => response.data);
  }

  async getCalendarFilling({
    channelId,
    scheduleId,
    periodStart,
    periodEnd,
  }: GetCalendarFillingParams): Promise<GetCalendarFillingResponse> {
    return this.axios
      .get<GetCalendarFillingResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/${scheduleId}/calendar_filling`,
        {
          params: {
            period_start: periodStart,
            period_end: periodEnd,
          },
        },
      )
      .then((response) => response.data);
  }

  async getCalendarFillingWeek({
    channelId,
    week,
    variants = false,
  }: GetCalendarFillingWeekParams): Promise<GetCalendarFillingWeekResponse> {
    return this.axios
      .get<GetCalendarFillingWeekResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/calendar_filling/week`,
        {
          params: {
            week,
            variants,
          },
        },
      )
      .then((response) => response.data);
  }

  async getScheduleEPG({
    channelId,
    scheduleId,
    periodStart,
    periodEnd,
  }: GetScheduleEPGParams): Promise<GetScheduleEPGResponse> {
    return this.axios
      .get<GetScheduleEPGResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/${scheduleId}/epg`,
        {
          params: {
            period_start: periodStart,
            period_end: periodEnd,
          },
        },
      )
      .then((response) => response.data);
  }

  async deleteSchedule({
    channelId,
    scheduleId,
    date,
  }: DeleteScheduleParams): Promise<DeleteScheduleResponse> {
    return this.axios
      .delete<DeleteScheduleResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/${scheduleId}/day`,
        {
          params: {
            period: date,
          },
        },
      )
      .then((response) => response.data);
  }

  async getAdBreaksList(channelId: string): Promise<GetAdBreaksListResponse> {
    return this.axios
      .get<GetAdBreaksListResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/adbreaks`,
      )
      .then((response) => response.data);
  }

  async getAdBreak(
    channelId: string,
    adbreakId: string,
  ): Promise<GetAdBreakResponse> {
    return this.axios
      .get<GetAdBreakResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/adbreak/${adbreakId}`,
      )
      .then((response) => response.data);
  }

  async deleteAdBreak(
    channelId: string,
    adBreakId: string,
  ): Promise<DeleteAdBreakResponse> {
    return this.axios
      .delete<DeleteAdBreakResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/adbreak/${adBreakId}`,
      )
      .then((response) => response.data);
  }

  async archiveAdbreak(
    channelId: string,
    adBreakId: string,
  ): Promise<ArchiveAdBreakResponse> {
    return this.axios
      .post<ArchiveAdBreakResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/adbreak/${adBreakId}`,
      )
      .then((response) => response.data);
  }

  async updateAdBreak(
    channelId: string,
    adbreakId: string,
    data: UpdatedAdBreak,
  ): Promise<GetAdBreakResponse> {
    return this.axios
      .put<GetAdBreakResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/adbreak/${adbreakId}`,
        data,
      )
      .then((response) => response.data);
  }

  async createAdBreak(
    channelId: string,
    data: UpdatedAdBreak,
  ): Promise<GetAdBreakResponse> {
    return this.axios
      .post<GetAdBreakResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/adbreak/`,
        data,
      )
      .then((response) => response.data);
  }

  async duplicateAdbreak(
    channelId: string,
    adbreakId: string,
  ): Promise<GetAdBreakResponse> {
    return this.axios
      .post<GetAdBreakResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/adbreak/${adbreakId}/duplicate`,
      )
      .then((response) => response.data);
  }

  async getBroadcasts(
    ad_service?: BroadcastAdService,
  ): Promise<GetBroadcastsResponse> {
    return this.axios
      .get<GetBroadcastsResponse>(`${this.routeBase}/admin/broadcasts`, {
        params: {
          ad_service,
        },
      })
      .then((response) => response.data);
  }

  async updateBroadcast(
    channelId: string,
    updatedBroadcast: OVPBroadcast,
  ): Promise<UpdateBroadcastResponse> {
    return this.axios
      .put<UpdateBroadcastResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/broadcast/${updatedBroadcast.uuid}`, // TODO demander broadcast au pluriel
        updatedBroadcast,
      )
      .then((response) => response.data);
  }

  async getPlaylistList(channelId: string): Promise<GetPlaylistListResponse> {
    return this.axios
      .get<GetPlaylistListResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/playlists`,
      )
      .then((response) => response.data);
  }

  async getPlaylist({
    channelId,
    playlistId,
  }: {
    channelId: string;
    playlistId: string;
  }): Promise<GetPlaylistResponse> {
    return this.axios
      .get<GetPlaylistResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/playlist/${playlistId}`,
      )
      .then((response) => response.data);
  }

  async deletePlaylist({
    channelId,
    playlistId,
  }: {
    channelId: string;
    playlistId: string;
  }): Promise<unknown> {
    return this.axios
      .delete(
        `${this.routeBase}/admin/fast_channel/${channelId}/playlist/${playlistId}`,
      )
      .then((response) => response.data);
  }

  async updatePlaylist({
    channelId,
    playlistId,
    updatedPlaylist,
  }: UpdatePlaylistParams): Promise<GetPlaylistResponse> {
    return this.axios
      .put<GetPlaylistResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/playlist/${playlistId}`,
        updatedPlaylist,
      )
      .then((response) => response.data);
  }

  async createPlaylist({
    channelId,
    updatedPlaylist,
  }: CreatePlaylistParams): Promise<GetPlaylistResponse> {
    return this.axios
      .post<GetPlaylistResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/playlist/`,
        updatedPlaylist,
      )
      .then((response) => response.data);
  }

  async duplicatePlaylist(
    channelId: string,
    playlistId: string,
  ): Promise<GetPlaylistResponse> {
    return this.axios
      .post<GetPlaylistResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/playlist/${playlistId}/duplicate`,
      )
      .then((response) => response.data);
  }

  async getTimeslotList(channelId: string): Promise<GetTimeslotList> {
    return this.axios
      .get<GetTimeslotList>(
        `${this.routeBase}/admin/fast_channel/${channelId}/timeslots`,
      )
      .then((response) => response.data);
  }

  async getTimeslot({
    channelId,
    timeslotId,
  }: {
    channelId: string;
    timeslotId: string;
  }): Promise<GetTimeslotResponse> {
    return this.axios
      .get<GetTimeslotResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/timeslot/${timeslotId}`,
      )
      .then((response) => response.data);
  }

  async deleteTimeslot({
    channelId,
    timeslotId,
  }: {
    channelId: string;
    timeslotId: string;
  }): Promise<unknown> {
    return this.axios
      .delete(
        `${this.routeBase}/admin/fast_channel/${channelId}/timeslot/${timeslotId}`,
      )
      .then((response) => response.data);
  }

  async archiveTimeslot({
    channelId,
    timeslotId,
  }: {
    channelId: string;
    timeslotId: string;
  }): Promise<EmptyObject> {
    return this.axios
      .post<EmptyObject>(
        `${this.routeBase}/admin/fast_channel/${channelId}/timeslot/${timeslotId}`,
      )
      .then((response) => response.data);
  }

  async createTimeslot({
    channelId,
    data,
  }: {
    channelId: string;
    data: UpdatedTimeslot;
  }): Promise<GetTimeslotResponse> {
    return this.axios
      .post<GetTimeslotResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/timeslot/`,
        data,
      )
      .then((response) => response.data);
  }

  async updateTimeslot({
    channelId,
    timeslotId,
    data,
  }: {
    channelId: string;
    timeslotId: string;
    data: UpdatedTimeslot;
  }): Promise<GetTimeslotResponse> {
    return this.axios
      .put<GetTimeslotResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/timeslot/${timeslotId}`,
        data,
      )
      .then((response) => response.data);
  }

  async duplicateTimeslot({
    channelId,
    timeslotId,
  }: {
    channelId: string;
    timeslotId: string;
  }): Promise<unknown> {
    return this.axios
      .post(
        `${this.routeBase}/admin/fast_channel/${channelId}/timeslot/${timeslotId}/duplicate`,
      )
      .then((response) => response.data);
  }

  async getTemplatesList(channelId: string): Promise<GetTemplatesListResponse> {
    return this.axios
      .get<GetTemplatesListResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/templates`,
      )
      .then((response) => response.data);
  }

  async duplicateTemplate(
    channelId: string,
    templateId: string,
  ): Promise<GetTemplateResponse> {
    return this.axios
      .post<GetTemplateResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/template/${templateId}/duplicate`,
      )
      .then((response) => response.data);
  }

  async deleteTemplate(
    channelId: string,
    templateId: string,
  ): Promise<unknown> {
    return this.axios
      .delete(
        `${this.routeBase}/admin/fast_channel/${channelId}/template/${templateId}`,
      )
      .then((response) => response.data);
  }

  async archiveTemplate(
    channelId: string,
    templateId: string,
  ): Promise<EmptyObject> {
    return this.axios
      .post<EmptyObject>(
        `${this.routeBase}/admin/fast_channel/${channelId}/template/${templateId}`,
      )
      .then((response) => response.data);
  }

  async templatesSchedules(
    channelId: string,
    data: SchedulingParameters,
  ): Promise<string> {
    return this.axios
      .post<string>(
        `${this.routeBase}/admin/fast_channel/${channelId}/templates_schedule`,
        data,
      )
      .then((response) => response.data);
  }

  async getTemplatesAssetList(
    channelId: string,
  ): Promise<GetTemplatesAssetListResponse> {
    return this.axios
      .get<GetTemplatesAssetListResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/templates_assets`,
      )
      .then((response) => response.data);
  }

  async getTemplate(
    channelId: string,
    templateId: string,
  ): Promise<GetTemplateResponse> {
    return this.axios
      .get<GetTemplateResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/template/${templateId}`,
      )
      .then((response) => response.data);
  }

  async createTemplate({
    channelId,
    data,
  }: {
    channelId: string;
    data: UpdatedTemplate;
  }): Promise<GetTemplateResponse> {
    return this.axios
      .post<GetTemplateResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/template/`,
        data,
      )
      .then((response) => response.data);
  }

  async updateTemplate({
    channelId,
    templateId,
    data,
  }: {
    channelId: string;
    templateId: string;
    data: UpdatedTemplate;
  }): Promise<GetTemplateResponse> {
    return this.axios
      .put<GetTemplateResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/template/${templateId}`,
        data,
      )
      .then((response) => response.data);
  }

  async deleteScheduleListTimeslot({
    channelId,
    scheduleId,
    periodStart,
    periodEnd,
  }: DeleteScheduleListTimeslotParams): Promise<DeleteScheduleListTimeslotResponse> {
    return this.axios
      .delete<DeleteScheduleListTimeslotResponse>(
        `${this.routeBase}/admin/fast_channel/${channelId}/schedule/${scheduleId}/schedules_lists`,
        {
          params: {
            period_start: periodStart,
            period_end: periodEnd,
          },
        },
      )
      .then((response) => response.data);
  }

  async getRatingList(): Promise<GetRatingListResponse> {
    return this.axios
      .get<GetRatingListResponse>(`${this.routeBase}/admin/ratings`)
      .then((response) => response.data);
  }
}

const FastAPI = new FastAPIClass({
  token: localStorage.token,
});

export default FastAPI;
